import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFormFieldMainValidationWithOnValueChangeValidation,
  useValidatableFormFieldFocus,
} from '../../../hooks';
import { FormFieldWrap, Select } from '@zooplus/ui-components';
import { SelectFormFieldValidator } from '../../../hooks/validation/validator/SelectFormFieldValidator';
import { FieldPropsWithOnChange } from './types';
import ContactReasons from '../../../constants/ContactReasons';

const reasonKeys = Object.values(ContactReasons);
type ReasonFieldProps = FieldPropsWithOnChange;

const ReasonField: React.FC<ReasonFieldProps> = ({ onChange }) => {
  const { t } = useTranslation();

  const label = t('form.fields.reason.label');
  const placeholder = t('form.fields.reason.placeholder');
  const errorMessage = t('form.fields.reason.error_message');
  const isRequired = true;
  const inputWrapperElementRef = useRef<HTMLDivElement>(null);

  const [reason, setReason] = useState('');

  const reasonTranslations = reasonKeys.reduce(
    (result, key) => {
      result[key] = t(`form.fields.reason.reasons.${key}`);
      return result;
    },
    {} as Record<string, string>,
  );

  const reasonsOptions = reasonKeys.map(
    (reasonKey) =>
      reasonTranslations[reasonKey] && (
        <option key={reasonKey} value={reasonKey}>
          {reasonTranslations[reasonKey]}
        </option>
      ),
  );

  const { hasError, validate } = useFormFieldMainValidationWithOnValueChangeValidation(
    new SelectFormFieldValidator(isRequired),
    { value: reason },
  );

  useValidatableFormFieldFocus(
    {
      formFieldSelector: 'select[name="reason"]',
      formFieldWrapperElementRef: inputWrapperElementRef,
    },
    {},
  );

  const updateValueCallback = useCallback((evt: ChangeEvent<HTMLSelectElement>) => {
    setReason(evt.target.value);
  }, []);
  const validateCallback = useCallback(() => validate(), [validate]);

  useEffect(() => {
    onChange(reason);
  }, [reason]);

  return (
    <div
      data-test="contactForm.field.reason"
      data-form-control-type="SELECT"
      className="contactForm__field"
      ref={inputWrapperElementRef}>
      <FormFieldWrap
        labelFor="reason"
        isRequired={isRequired}
        labelText={label}
        errorMessage={hasError && errorMessage}>
        <Select
          hasError={hasError}
          onChange={updateValueCallback}
          selectProps={{
            id: 'reason',
            name: 'reason',
            placeholder: placeholder,
            value: reason as string,
            onBlur: validateCallback,
          }}>
          <option key={''} value="" disabled>
            {placeholder}
          </option>
          {reasonsOptions}
        </Select>
        <input name="subject" type="text" hidden defaultValue={reasonTranslations[reason] || ''} />
      </FormFieldWrap>
    </div>
  );
};

export default ReasonField;
